// 🚀 OPTIMIZED SIMPLE CACHE SYSTEM - CURSOR V97.0
// Enhanced for faster page transitions and better performance
// Now extends PersistentCache for backward compatibility

import { persistentCache } from './persistentCache';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
  version?: string;
}

interface CacheConfig {
  maxAge: number;
  staleWhileRevalidate?: number;
  version?: string;
}

class SimpleCache {
  // 🚀 V97.0: Delegate to persistent cache for backward compatibility
  private cache = persistentCache;
  private configs = new Map<string, CacheConfig>();

  // 🚀 V97.0: Delegate to persistent cache
  set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000, version?: string): void {
    this.cache.set(key, data, ttl, version);
  }

  get<T>(key: string): T | null {
    return this.cache.get(key);
  }

  // 🚀 V97.0: Delegate to persistent cache
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  // 🚀 V97.0: Delegate to persistent cache
  registerCache(key: string, config: CacheConfig): void {
    this.cache.registerCache(key, config);
  }

  // 🚀 V97.0: Delegate to persistent cache
  async getWithBackgroundRefresh<T>(
    key: string, 
    fetchFn: () => Promise<T>, 
    config?: CacheConfig
  ): Promise<T> {
    return this.cache.getWithBackgroundRefresh(key, fetchFn, config);
  }

  // 🚀 V97.0: Delegate to persistent cache
  setBatch(entries: Array<{ key: string; data: any; ttl?: number }>): void {
    this.cache.setBatch(entries);
  }

  // 🚀 V97.0: Delegate to persistent cache
  getStats(): { size: number; keys: string[] } {
    const stats = this.cache.getStats();
    return {
      size: stats.memorySize,
      keys: stats.memoryKeys
    };
  }

  // 🚀 V97.0: Delegate to persistent cache
  cleanup(): void {
    this.cache.cleanup();
  }
}

// 🚀 V97.0: Global cache instance with persistent backing
export const simpleCache = new SimpleCache();

// 🚀 V97.0: Auto-cleanup every 5 minutes (delegated to persistent cache)
setInterval(() => {
  simpleCache.cleanup();
}, 5 * 60 * 1000);

export default simpleCache;



